/**
 * Workplace details service
 * Handles all business logic for workplace settings including CRUD operations and file uploads
 */

import { supabase } from '../../integrations/supabase/client';
import type {
  WorkplaceDetails,
  WorkplaceDetailsUpdate,
  LogoUploadResult,
  ServiceResponse,
  WorkplaceServiceError,
} from './types';
import { validateLogoFile, validateImageDimensions } from '../../utils/validation';

/**
 * Error mapping for RPC function errors
 */
const mapRpcError = (error: string): WorkplaceServiceError => {
  if (error.includes('insufficient_permissions')) return 'insufficient_permissions';
  if (error.includes('invalid_time_format')) return 'invalid_time_format';
  if (error.includes('invalid_date_format')) return 'invalid_date_format';
  if (error.includes('invalid_country_code')) return 'invalid_country_code';
  if (error.includes('invalid_currency_code')) return 'invalid_currency_code';
  if (error.includes('invalid_theme_color')) return 'invalid_theme_color';
  return 'unknown_error';
};

/**
 * Get current company's workplace details
 */
export const getWorkplaceDetails = async (): Promise<ServiceResponse<WorkplaceDetails>> => {
  try {
    const { data, error } = await supabase.rpc('get_workplace_details');

    if (error) {
      console.error('Error fetching workplace details:', error);
      return {
        success: false,
        error: `Failed to fetch workplace details: ${error.message}`,
      };
    }

    if (!data) {
      return {
        success: false,
        error: 'No workplace details found',
      };
    }

    return {
      success: true,
      data: data as unknown as WorkplaceDetails,
    };
  } catch (error) {
    console.error('Network error fetching workplace details:', error);
    return {
      success: false,
      error: 'Network error occurred while fetching workplace details',
    };
  }
};

/**
 * Update workplace details
 */
export const updateWorkplaceDetails = async (
  updates: WorkplaceDetailsUpdate
): Promise<ServiceResponse<WorkplaceDetails>> => {
  try {
    // Handle logo upload first if provided
    let logoUrl: string | undefined;
    if (updates.logoFile) {
      const uploadResult = await uploadLogo(updates.logoFile);
      if (!uploadResult.success) {
        return {
          success: false,
          error: uploadResult.error || 'Failed to upload logo',
        };
      }
      logoUrl = uploadResult.data?.url;
    }

    // Prepare RPC parameters
    const rpcParams = {
      p_business_name: updates.businessName || null,
      p_time_format: updates.timeFormat || null,
      p_date_format: updates.dateFormat || null,
      p_country_code: updates.countryCode || null,
      p_currency_code: updates.currencyCode || null,
      p_theme_color: updates.themeColor || null,
      p_logo_url: logoUrl || null,
    };

    const { data, error } = await supabase.rpc('update_workplace_details', rpcParams);

    if (error) {
      console.error('Error updating workplace details:', error);
      const errorCode = mapRpcError(error.message);
      return {
        success: false,
        error: `Failed to update workplace details: ${error.message}`,
      };
    }

    if (!data) {
      return {
        success: false,
        error: 'No data returned from update operation',
      };
    }

    return {
      success: true,
      data: data as unknown as WorkplaceDetails,
    };
  } catch (error) {
    console.error('Network error updating workplace details:', error);
    return {
      success: false,
      error: 'Network error occurred while updating workplace details',
    };
  }
};

/**
 * Upload company logo to storage
 */
export const uploadLogo = async (
  file: File,
  companyId?: string
): Promise<ServiceResponse<LogoUploadResult>> => {
  try {
    // Validate file
    const fileValidation = validateLogoFile(file);
    if (!fileValidation.isValid) {
      return {
        success: false,
        error: fileValidation.error || 'Invalid file',
      };
    }

    // Validate image dimensions
    const dimensionValidation = await validateImageDimensions(file);
    if (!dimensionValidation.isValid) {
      return {
        success: false,
        error: dimensionValidation.error || 'Invalid image dimensions',
      };
    }

    // Get current user's company ID if not provided
    let targetCompanyId = companyId;
    if (!targetCompanyId) {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return {
          success: false,
          error: 'User not authenticated',
        };
      }

      // Get user's company ID from profile
      const { data: profile } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('user_id', user.id)
        .single();

      if (!profile?.company_id) {
        return {
          success: false,
          error: 'User not associated with a company',
        };
      }

      targetCompanyId = profile.company_id;
    }

    // Generate unique filename
    const fileExtension = file.name.split('.').pop()?.toLowerCase() || 'jpg';
    const fileName = `logo-${Date.now()}.${fileExtension}`;
    const filePath = `${targetCompanyId}/logos/${fileName}`;

    // Upload to storage
    const { data: uploadData, error: uploadError } = await supabase.storage
      .from('company-assets')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false,
      });

    if (uploadError) {
      console.error('Error uploading logo:', uploadError);
      return {
        success: false,
        error: `Failed to upload logo: ${uploadError.message}`,
      };
    }

    // Get public URL
    const { data: urlData } = supabase.storage
      .from('company-assets')
      .getPublicUrl(filePath);

    if (!urlData?.publicUrl) {
      return {
        success: false,
        error: 'Failed to get public URL for uploaded logo',
      };
    }

    return {
      success: true,
      data: {
        url: urlData.publicUrl,
        path: filePath,
        size: file.size,
        contentType: file.type,
      },
    };
  } catch (error) {
    console.error('Network error uploading logo:', error);
    return {
      success: false,
      error: 'Network error occurred while uploading logo',
    };
  }
};

/**
 * Delete company logo from storage and database
 */
export const deleteLogo = async (): Promise<ServiceResponse<void>> => {
  try {
    // Get current logo URL to extract path
    const detailsResult = await getWorkplaceDetails();
    if (!detailsResult.success || !detailsResult.data?.logo_url) {
      return {
        success: false,
        error: 'No logo found to delete',
      };
    }

    const logoUrl = detailsResult.data.logo_url;
    
    // Extract path from URL
    const urlParts = logoUrl.split('/');
    const bucketIndex = urlParts.findIndex(part => part === 'company-assets');
    if (bucketIndex === -1 || bucketIndex >= urlParts.length - 1) {
      return {
        success: false,
        error: 'Invalid logo URL format',
      };
    }

    const logoPath = urlParts.slice(bucketIndex + 1).join('/');

    // Delete from storage
    const { error: storageError } = await supabase.storage
      .from('company-assets')
      .remove([logoPath]);

    if (storageError) {
      console.error('Error deleting logo from storage:', storageError);
      // Continue with database update even if storage deletion fails
    }

    // Remove logo URL from database
    const { data, error: rpcError } = await supabase.rpc('delete_workplace_logo');

    if (rpcError) {
      console.error('Error removing logo URL from database:', rpcError);
      return {
        success: false,
        error: `Failed to remove logo: ${rpcError.message}`,
      };
    }

    return {
      success: true,
    };
  } catch (error) {
    console.error('Network error deleting logo:', error);
    return {
      success: false,
      error: 'Network error occurred while deleting logo',
    };
  }
};

/**
 * Validate workplace details data
 */
export const validateWorkplaceDetailsData = async (
  data: WorkplaceDetailsUpdate
): Promise<ServiceResponse<boolean>> => {
  try {
    // Basic validation
    if (data.businessName && data.businessName.trim().length < 2) {
      return {
        success: false,
        error: 'Business name must be at least 2 characters long',
      };
    }

    if (data.timeFormat && !['12h', '24h'].includes(data.timeFormat)) {
      return {
        success: false,
        error: 'Time format must be either 12h or 24h',
      };
    }

    if (data.dateFormat && !['DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD'].includes(data.dateFormat)) {
      return {
        success: false,
        error: 'Invalid date format',
      };
    }

    if (data.countryCode && (data.countryCode.length !== 2 || !/^[A-Z]{2}$/.test(data.countryCode))) {
      return {
        success: false,
        error: 'Country code must be a 2-character ISO code',
      };
    }

    if (data.currencyCode && (data.currencyCode.length !== 3 || !/^[A-Z]{3}$/.test(data.currencyCode))) {
      return {
        success: false,
        error: 'Currency code must be a 3-character ISO code',
      };
    }

    if (data.themeColor && !/^#[0-9A-Fa-f]{6}$/.test(data.themeColor)) {
      return {
        success: false,
        error: 'Theme color must be a valid hex color',
      };
    }

    if (data.logoFile) {
      const fileValidation = validateLogoFile(data.logoFile);
      if (!fileValidation.isValid) {
        return {
          success: false,
          error: fileValidation.error || 'Invalid logo file',
        };
      }

      const dimensionValidation = await validateImageDimensions(data.logoFile);
      if (!dimensionValidation.isValid) {
        return {
          success: false,
          error: dimensionValidation.error || 'Invalid image dimensions',
        };
      }
    }

    return {
      success: true,
      data: true,
    };
  } catch (error) {
    console.error('Error validating workplace details:', error);
    return {
      success: false,
      error: 'Validation error occurred',
    };
  }
};

/**
 * Get company logo URL by company ID
 */
export const getCompanyLogoUrl = async (companyId: string): Promise<string | null> => {
  try {
    const { data } = await supabase
      .from('company_settings')
      .select('logo_url')
      .eq('company_id', companyId)
      .single();

    return data?.logo_url || null;
  } catch (error) {
    console.error('Error fetching company logo URL:', error);
    return null;
  }
};

/**
 * Workplace service object with all methods
 */
export const workplaceService = {
  getWorkplaceDetails,
  updateWorkplaceDetails,
  uploadLogo,
  deleteLogo,
  validateWorkplaceDetails: validateWorkplaceDetailsData,
  getCompanyLogoUrl,
};

export default workplaceService;
